import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useRentmanContactPersons } from '../hooks/useRentmanContactPersons';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Input } from './ui/input';
import { Label } from './ui/label';

const PHONE_PATTERN = '^\\+?[0-9()\\-\\s]{6,20}$';
const PHONE_REGEX = /^\+?[0-9()\-\s]{6,20}$/;

// Memoized contact person item component
const ContactPersonCommandItem = React.memo(({ contactPerson, selectedContactPerson, onSelect, style }) => (
  <div style={style}>
    <CommandItem
      value={contactPerson.displayname || contactPerson.name}
      onSelect={() => onSelect(contactPerson)}
      className="cursor-pointer"
    >
      {contactPerson.displayname || contactPerson.name}
      <Check
        className={cn(
          "ml-auto h-4 w-4",
          selectedContactPerson?.id === contactPerson.id ? "opacity-100" : "opacity-0"
        )}
      />
    </CommandItem>
  </div>
));

ContactPersonCommandItem.displayName = 'ContactPersonCommandItem';

export function ContactPersonSelect({ onSelect, selectedContactPerson, selectedCustomerId, isCustomerCustom = false, className }) {
  const { contactPersons, loading, error } = useRentmanContactPersons(selectedCustomerId);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef(null);
  const [listKey, setListKey] = useState(0);
  const [useCustomContactPerson, setUseCustomContactPerson] = useState(false);
  const [customContactPerson, setCustomContactPerson] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [hasValidated, setHasValidated] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const forceManualContact = isCustomerCustom;

  useEffect(() => {
    if (forceManualContact && !useCustomContactPerson) {
      setUseCustomContactPerson(true);
    }
  }, [forceManualContact, useCustomContactPerson]);

  // Set custom mode if contact person is custom
  useEffect(() => {
    if (selectedContactPerson?.isCustom && !useCustomContactPerson) {
      setUseCustomContactPerson(true);
      setCustomContactPerson({
        firstName: selectedContactPerson.firstName || '',
        lastName: selectedContactPerson.lastName || '',
        email: selectedContactPerson.email || '',
        phone: selectedContactPerson.phone || '',
      });
    }
    // Mark as hydrated after first render
    if (!isHydrated) {
      setIsHydrated(true);
    }
  }, [selectedContactPerson, useCustomContactPerson, isHydrated]);

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setListKey(prev => prev + 1);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [open]);

  // Memoize filtered contact persons
  const filteredContactPersons = useMemo(() => {
    if (!contactPersons?.length) return [];
    const query = searchQuery.toLowerCase().trim();
    
    const filtered = query === "" 
      ? contactPersons 
      : contactPersons.filter(contactPerson => {
          const searchableText = (contactPerson.displayname || contactPerson.name || "").toLowerCase();
          return searchableText.includes(query);
        });
    
    return filtered;
  }, [contactPersons, searchQuery]);

  // Memoize the selection handler
  const handleSelect = useCallback((contactPerson) => {
    onSelect(contactPerson);
    setOpen(false);
  }, [onSelect]);

  // Handle custom contact person selection
  useEffect(() => {
    if (useCustomContactPerson) {
      const customPhone = (customContactPerson.phone || '').trim();
      const hasValidPhone = PHONE_REGEX.test(customPhone);
      if (customContactPerson.firstName.trim() && 
          customContactPerson.lastName.trim() && 
          customContactPerson.email.trim() &&
          hasValidPhone) {
        onSelect({
          id: 'custom',
          name: `${customContactPerson.firstName} ${customContactPerson.lastName}`.trim(),
          displayname: `${customContactPerson.firstName} ${customContactPerson.lastName}`.trim(),
          isCustom: true,
          ...customContactPerson
        });
      } else {
        // Only clear if the user is actively editing the custom fields (not on mount)
        // Do not call onSelect(null) here automatically
      }
    }
  }, [useCustomContactPerson, customContactPerson, onSelect]);

  // Reset custom fields if toggling back to dropdown
  useEffect(() => {
    if (!useCustomContactPerson) {
      setCustomContactPerson({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      });
      // Do not call onSelect(null) here automatically
    }
  }, [useCustomContactPerson]);

  // Set hasValidated to true after first load
  useEffect(() => {
    if (!loading && !hasValidated) {
      setHasValidated(true);
    }
  }, [loading, hasValidated]);

  const parentRef = React.useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredContactPersons.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 5,
  });

  // Calculate dynamic height based on number of items
  const dynamicHeight = Math.min(filteredContactPersons.length * 36, 250); // 36px per item, max 250px

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive p-4">
        Error loading contact persons: {error}
      </div>
    );
  }

  return (
    <div className={cn("w-full space-y-2", className)}>
      {!forceManualContact && !selectedContactPerson?.isCustom && (
        <div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={useCustomContactPerson}
              onChange={e => setUseCustomContactPerson(e.target.checked)}
              className="accent-primary"
            />
            Contact person not found? Enter manually
          </label>
        </div>
      )}
      {(useCustomContactPerson || forceManualContact) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="contactFirstName">First Name <span className="text-destructive">*</span></Label>
            <Input
              id="contactFirstName"
              value={customContactPerson.firstName}
              onChange={(e) => setCustomContactPerson(prev => ({ ...prev, firstName: e.target.value }))}
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="contactLastName">Last Name <span className="text-destructive">*</span></Label>
            <Input
              id="contactLastName"
              value={customContactPerson.lastName}
              onChange={(e) => setCustomContactPerson(prev => ({ ...prev, lastName: e.target.value }))}
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="contactEmail">Email <span className="text-destructive">*</span></Label>
            <Input
              id="contactEmail"
              type="email"
              value={customContactPerson.email}
              onChange={(e) => setCustomContactPerson(prev => ({ ...prev, email: e.target.value }))}
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="contactPhone">Phone number <span className="text-destructive">*</span></Label>
            <Input
              id="contactPhone"
              type="tel"
              value={customContactPerson.phone}
              onChange={(e) => setCustomContactPerson(prev => ({ ...prev, phone: e.target.value }))}
              className="mt-1"
              pattern={PHONE_PATTERN}
              title="Use a valid phone number (6-20 digits, optional +, spaces, (), -)."
              required
            />
          </div>
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={!selectedCustomerId}
            >
              {selectedContactPerson
                ? selectedContactPerson.displayname || selectedContactPerson.name
                : "Select contact person..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" side="bottom">
            <Command shouldFilter={false}>
              <CommandInput 
                ref={inputRef}
                placeholder="Search contact persons..." 
                className="h-9"
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList key={listKey} className="max-h-[300px]">
                {filteredContactPersons.length === 0 ? (
                  <CommandEmpty>No contact persons found.</CommandEmpty>
                ) : (
                  <CommandGroup className="overflow-hidden">
                    <div
                      ref={parentRef}
                      className="relative overflow-auto"
                      style={{ height: `${dynamicHeight}px` }}
                    >
                      <div
                        style={{
                          height: `${rowVirtualizer.getTotalSize()}px`,
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                          const contactPerson = filteredContactPersons[virtualRow.index];
                          return (
                            <ContactPersonCommandItem
                              key={contactPerson.id}
                              contactPerson={contactPerson}
                              selectedContactPerson={selectedContactPerson}
                              onSelect={handleSelect}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualRow.start}px)`,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
} 